import React, { forwardRef, useState, useEffect, useLayoutEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, X, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { useChat } from "@/hooks/useChat";
import api, { notifyError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ChatThread } from "@/components/ChatThread";
import { ImageAttach, ImagePreview } from "@/components/ImageAttach";
import { MicButton } from "@/components/MicButton";
import { UsageNudge } from "@/components/UsageNudge";
import { Headphones, Telescope } from "lucide-react";

const MODES = ["understand", "compare", "decide", "act"];
const MODE_TO_TYPE = { understand: "problem", compare: "comparison", decide: "plan", act: "guide" };

export const Hero = forwardRef(function Hero({ prompt, setPrompt, mode, setMode, autoPrompt, autoMode }, ref) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { messages, send, sendImage, retry, streaming, followups, conversationId } = useChat();
  const isAuthed = user && typeof user === "object";
  const hasChat = messages.length > 0;
  const autoRan = useRef(false);
  const composerRef = useRef(null);
  const [images, setImages] = useState([]);
  const [voiceMode, setVoiceMode] = useState(false);
  const [deepResearch, setDeepResearch] = useState(false);
  const isPaid = isAuthed && ["basic", "plus", "pro"].includes(user.membership_tier);

  // When the homepage becomes a chat (fixed bottom composer), publish the composer's
  // ACTUAL height so the floating Help widget always sits above it (never overlaps),
  // on any viewport. Reset to 0 when not in chat mode.
  useLayoutEffect(() => {
    const root = document.documentElement;
    const el = composerRef.current;
    if (!hasChat || !el) { root.style.setProperty("--evermin-composer", "0px"); return; }
    const update = () => {
      const gap = Math.max(0, window.innerHeight - el.getBoundingClientRect().top);
      root.style.setProperty("--evermin-composer", `${gap}px`);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update);
    return () => { ro.disconnect(); window.removeEventListener("resize", update); root.style.setProperty("--evermin-composer", "0px"); };
  }, [hasChat]);

  useEffect(() => {
    if (autoRan.current || !autoPrompt) return;
    autoRan.current = true;
    if (autoMode) setMode(autoMode);
    send(autoPrompt, autoMode || "understand");
    window.history.replaceState({}, document.title, "/");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPrompt, autoMode]);

  const submit = (e) => {
    e?.preventDefault();
    if (streaming) return;
    const clean = prompt.trim();
    if (images.length) {
      sendImage(images, clean);
      setImages([]);
      setPrompt("");
      return;
    }
    if (!clean) return;
    send(clean, mode, deepResearch);
    setPrompt("");
  };

  const saveMessage = async (msg) => {
    if (!isAuthed) {
      toast.info(t("result_login_to_save"));
      return false;
    }
    const userPrompt = [...messages].reverse().find((m) => m.role === "user");
    const category = msg.metadata?.category;
    try {
      await api.post("/saved-items", {
        type: MODE_TO_TYPE[mode] || "problem",
        title: (userPrompt?.content || "EVERMIN answer").slice(0, 120),
        content: msg.content,
        tags: category ? [mode, category] : [mode],
        metadata: msg.metadata || {},
      });
      toast.success(t("result_saved"));
      return true;
    } catch (err) {
      notifyError(err, t);
      return false;
    }
  };

  return (
    <section className="relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
      </div>

      <div className={`mx-auto max-w-3xl px-4 pb-8 pt-16 text-center sm:px-6 sm:pt-24 ${hasChat ? "pb-44 sm:pb-40" : ""}`}>
        <div className="animate-rise inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-1.5 text-xs font-medium text-slate-600 shadow-sm">
          <Sparkles className="h-3.5 w-3.5 text-primary" />
          {t("hero_badge")}
        </div>

        <h1 className="animate-rise text-balance font-display mt-6 text-4xl font-extrabold leading-[1.04] tracking-tight text-slate-950 sm:text-5xl lg:text-6xl" style={{ animationDelay: "60ms" }}>
          {t("headline")}
        </h1>
        <p className="animate-rise mx-auto mt-5 max-w-xl text-base text-slate-500 sm:text-lg" style={{ animationDelay: "120ms" }}>
          {t("brand_tagline")}
        </p>

        <form ref={composerRef} onSubmit={submit} className={hasChat ? "fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur" : "animate-rise mt-9"} style={hasChat ? undefined : { animationDelay: "180ms" }}>
          <div className={`rounded-2xl border bg-white transition-all focus-within:border-primary/40 focus-within:shadow-premium-lg focus-within:ring-2 focus-within:ring-primary/20 ${hasChat ? "mx-auto max-w-3xl border-slate-200/80 p-2.5 shadow-premium-lg" : "border-primary/25 p-3 shadow-premium-lg ring-1 ring-primary/15"}`}>
            {!hasChat && (
              <div className="mb-3 flex flex-wrap items-center justify-center gap-2">
                {MODES.map((m) => (
                  <button key={m} type="button" data-testid={`mode-${m}`} onClick={() => setMode(m)}
                    className={`min-h-[36px] rounded-full px-4 text-sm font-medium transition-colors ${mode === m ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                    {t(`mode_${m}`)}
                  </button>
                ))}
              </div>
            )}
            <ImagePreview files={images} onRemove={(i) => setImages((p) => p.filter((_, idx) => idx !== i))} />
            <div className="flex flex-wrap items-end gap-2 sm:flex-nowrap">
              <ImageAttach onSelect={(fs) => setImages((p) => [...p, ...fs].slice(0, 4))} disabled={streaming || images.length >= 4} />
              <MicButton onTranscript={(txt) => setPrompt(txt)} disabled={streaming} />
              <button type="button" onClick={() => setVoiceMode((v) => !v)} data-testid="voice-mode-toggle"
                aria-label="Voice mode" title={voiceMode ? "Voice Mode on — answers are read aloud" : "Turn on Voice Mode"}
                className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full transition-colors ${voiceMode ? "bg-primary text-white" : "text-slate-500 hover:bg-slate-100"}`}>
                <Headphones className="h-4 w-4" />
              </button>
              {isPaid && (
                <button type="button" onClick={() => setDeepResearch((v) => !v)} data-testid="deep-research-toggle"
                  aria-pressed={deepResearch} aria-label={t("deep_research")} title={t("deep_research_hint")}
                  className={`flex h-10 flex-shrink-0 items-center gap-1.5 rounded-full px-3 text-xs font-semibold transition-colors ${deepResearch ? "bg-primary text-white" : "text-slate-500 hover:bg-slate-100"}`}>
                  <Telescope className="h-4 w-4" /> <span className="hidden sm:inline">{t("deep_research")}</span>
                </button>
              )}
              <textarea
                ref={ref}
                data-testid="hero-search-input"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) submit(e); }}
                rows={2}
                placeholder={images.length ? t("ask_about_image_ph") : hasChat ? t("chat_placeholder") : t("input_placeholder")}
                className="order-first min-h-[56px] w-full resize-none rounded-xl bg-transparent px-3 py-3 text-left text-base text-slate-900 placeholder:text-slate-400 focus:outline-none sm:order-none sm:w-auto sm:flex-1"
              />
              {prompt && (
                <button type="button" onClick={() => setPrompt("")} data-testid="hero-clear-btn" aria-label={t("clear")} className="mb-1 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600">
                  <X className="h-4 w-4" />
                </button>
              )}
              <Button type="submit" data-testid="hero-submit-btn" disabled={(!prompt.trim() && !images.length) || streaming} className="mb-1 ml-auto h-12 flex-shrink-0 rounded-xl px-5 font-semibold sm:ml-0">
                {streaming ? t("asking") : (<span className="flex items-center gap-1.5">{hasChat ? t("send") : t("ask_button")}<ArrowRight className="h-4 w-4" /></span>)}
              </Button>
            </div>
          </div>
          {!hasChat ? (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs text-slate-400">{t("input_hint")}</p>
              <UsageNudge />
            </div>
          ) : (
            <div className="mt-2 flex justify-end"><UsageNudge /></div>
          )}
        </form>

        {hasChat && (
          <div className="mt-8">
            <ChatThread
              messages={messages}
              onSaveMessage={saveMessage}
              isAuthed={isAuthed}
              followups={followups}
              onFollowup={(text) => send(text, mode)}
              onRetry={retry}
              streaming={streaming}
              voiceMode={voiceMode}
            />
            {isAuthed && conversationId && (
              <div className="mt-4 flex justify-center">
                <Button asChild variant="ghost" size="sm" className="gap-1.5 text-slate-500" data-testid="open-full-chat-btn">
                  <Link to={`/chat/${conversationId}`}>
                    <MessageSquare className="h-4 w-4" /> {t("open_full_chat")}
                  </Link>
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
});
